import React, { useMemo, useState } from 'react';
import { Link, useNavigate, useParams } from 'react-router-dom';
import { useQuery, useQueryClient } from '@tanstack/react-query';
import { Alert, Button, Card, Drawer, Select, Space, Spin, Typography } from 'antd';
import { EnterprisePageHeader, WorkspaceLayout } from '@/components/enterprise';
import BatterySchematicView, { type BatterySchematicCard } from '@/components/pmv/BatterySchematicView';
import PmvInstallToPositionModal from '@/components/pmv/PmvInstallToPositionModal';
import pmvService from '@/services/pmv.service';
import { formatHireEquipmentLabel } from '@/utils/pmvHireUi';

const { Text } = Typography;

const PmvBatterySchematicPage: React.FC = () => {
  const { id } = useParams<{ id: string }>();
  const navigate = useNavigate();
  const [manualId, setManualId] = useState<string | undefined>();
  const [equipSearch, setEquipSearch] = useState('');
  const profileId = id || manualId || '';
  const qc = useQueryClient();
  const [installCard, setInstallCard] = useState<BatterySchematicCard | null>(null);
  const [selected, setSelected] = useState<BatterySchematicCard | null>(null);

  const profilesQ = useQuery({
    queryKey: ['pmv-schematic-profiles', equipSearch],
    queryFn: () =>
      pmvService.listProfiles({ page: 1, pageSize: 100, q: equipSearch || undefined }),
    enabled: !id,
    staleTime: 30_000,
  });

  const equipmentOptions = useMemo(
    () =>
      (profilesQ.data?.data ?? []).map((p) => ({
        value: p.id,
        label: formatHireEquipmentLabel(p),
      })),
    [profilesQ.data]
  );

  const q = useQuery({
    queryKey: ['pmv-battery-schematic', profileId],
    queryFn: () => pmvService.getBatterySchematic(profileId),
    enabled: Boolean(profileId),
    retry: false,
  });

  const refresh = () => {
    void qc.invalidateQueries({ queryKey: ['pmv-battery-schematic', profileId] });
    void qc.invalidateQueries({ queryKey: ['pmv-tyre-schematic', profileId] });
  };

  const onSelectCard = (card: BatterySchematicCard) => {
    if (card.status === 'VACANT') setInstallCard(card);
    else if (card.component_id) setSelected(card);
  };

  return (
    <WorkspaceLayout>
      <EnterprisePageHeader
        documentLabel="Battery schematic"
        subtitle="Operational battery bank — voltage, SOH, health source provenance"
        primaryActions={
          <Space wrap>
            {profileId ? (
              <Button>
                <Link to={`/asset/pmv/${profileId}`}>Equipment passport</Link>
              </Button>
            ) : null}
            <Button>
              <Link to="/asset/pmv/hub">PMV hub</Link>
            </Button>
          </Space>
        }
      />

      {!id && (
        <Card size="small" style={{ marginBottom: 16 }} title="Open by equipment">
          <Space wrap style={{ width: '100%' }}>
            <Select
              showSearch
              allowClear
              placeholder="Search fleet / asset code"
              style={{ minWidth: 360, maxWidth: 520 }}
              value={manualId}
              onSearch={setEquipSearch}
              onChange={(v) => setManualId(v)}
              filterOption={false}
              options={equipmentOptions}
              notFoundContent={profilesQ.isLoading ? 'Loading…' : 'No equipment'}
            />
            <Button
              type="primary"
              disabled={!manualId}
              onClick={() => manualId && navigate(`/asset/pmv/${manualId}/batteries`)}
            >
              Open schematic
            </Button>
          </Space>
        </Card>
      )}

      {q.isError && (
        <Alert
          type="error"
          showIcon
          style={{ marginBottom: 16 }}
          message="Could not load battery schematic"
          description={(q.error as Error)?.message}
        />
      )}

      <Card>
        {q.isLoading ? (
          <div style={{ textAlign: 'center', padding: 48 }}>
            <Spin />
          </div>
        ) : (
          <BatterySchematicView
            data={q.data}
            pmvProfileId={profileId || undefined}
            allowVacantSelect
            onLayoutApplied={refresh}
            onSelectCard={onSelectCard}
          />
        )}
        {q.data?.note && (
          <Text type="secondary" style={{ display: 'block', marginTop: 16, fontSize: 12 }}>
            {q.data.note}
          </Text>
        )}
      </Card>

      <PmvInstallToPositionModal
        open={Boolean(installCard)}
        profileId={profileId}
        family="BATTERY"
        position={
          installCard
            ? {
                position_id: installCard.position_id,
                position_code: installCard.position_code,
                display_label: installCard.display_label,
                family: 'BATTERY',
              }
            : null
        }
        onClose={() => setInstallCard(null)}
        onInstalled={refresh}
      />

      <Drawer
        open={Boolean(selected)}
        onClose={() => setSelected(null)}
        title={selected?.component_number || 'Battery'}
        width={360}
      >
        {selected ? (
          <Space direction="vertical">
            <Text>
              {selected.display_label} · {selected.condition_code || '—'}
            </Text>
            <Text type="secondary">
              SOH {selected.latest_soh_pct != null ? `${selected.latest_soh_pct}%` : '—'}
            </Text>
            <Button
              type="primary"
              disabled={!selected.component_id}
              onClick={() => navigate(`/asset/pmv/components/${selected.component_id}`)}
            >
              Open Component Passport
            </Button>
          </Space>
        ) : null}
      </Drawer>
    </WorkspaceLayout>
  );
};

export default PmvBatterySchematicPage;
